/**
 * CR426: Unified deal source mode hook.
 * 
 * Manages Sample vs Live mode, persists to localStorage,
 * and resolves the active deal + meta for editor preview.
 */

import { useState, useCallback, useMemo, useEffect } from "react";
import { useDealCardEditorSampleDeal, type EditorSampleDealMeta } from "./useDealCardEditorSampleDeal";
import { useDealCardEditorLiveDeals, useDealCardEditorLiveDeal, type LiveDealListItem } from "./useDealCardEditorLiveDeals";

// ================================================================
// TYPES
// ================================================================

export type DealSourceMode = "sample" | "live";

export interface DealSourceState {
  mode: DealSourceMode;
  setMode: (mode: DealSourceMode) => void;
  
  // Resolved deal for preview
  deal: Record<string, unknown> | null;
  meta: EditorSampleDealMeta | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
  
  // Live deals list (for picker)
  liveDeals: LiveDealListItem[];
  liveDealsLoading: boolean;
  liveSearch: string;
  setLiveSearch: (s: string) => void;
  selectedLiveDealId: string | null;
  setSelectedLiveDealId: (id: string | null) => void;
}

// ================================================================
// PERSISTENCE KEYS
// ================================================================

const MODE_KEY_PREFIX = "dealSource:mode:";
const LIVE_DEAL_KEY_PREFIX = "dealSource:liveDealId:";

function storageKey(prefix: string, env: string, dealType: string): string {
  return `${prefix}${env}:${dealType}`;
}

function getStored(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}

function setStored(key: string, value: string) {
  try { localStorage.setItem(key, value); } catch { /* ignore */ }
}

// ================================================================
// HOOK
// ================================================================

export function useDealSourceMode(
  environment: string = "staging",
  dealType: string | null = null
): DealSourceState {
  // Mode
  const modeKey = dealType ? storageKey(MODE_KEY_PREFIX, environment, dealType) : "";
  const [mode, setModeRaw] = useState<DealSourceMode>(() => {
    const stored = modeKey ? getStored(modeKey) : null;
    return (stored === "live" ? "live" : "sample") as DealSourceMode;
  });
  
  const setMode = useCallback((m: DealSourceMode) => {
    setModeRaw(m);
    if (modeKey) setStored(modeKey, m);
  }, [modeKey]);

  // Live deal selection
  const liveDealKey = dealType ? storageKey(LIVE_DEAL_KEY_PREFIX, environment, dealType) : "";
  const [selectedLiveDealId, setSelectedLiveDealIdRaw] = useState<string | null>(() => {
    return liveDealKey ? getStored(liveDealKey) : null;
  });
  const [liveSearch, setLiveSearch] = useState("");

  const setSelectedLiveDealId = useCallback((id: string | null) => {
    setSelectedLiveDealIdRaw(id);
    if (liveDealKey) {
      if (id) setStored(liveDealKey, id);
      else try { localStorage.removeItem(liveDealKey); } catch { /* ignore */ }
    }
  }, [liveDealKey]);

  // Reset when deal type / env changes
  useEffect(() => {
    const stored = modeKey ? getStored(modeKey) : null;
    setModeRaw((stored === "live" ? "live" : "sample") as DealSourceMode);
    const storedDeal = liveDealKey ? getStored(liveDealKey) : null;
    setSelectedLiveDealIdRaw(storedDeal);
    setLiveSearch("");
  }, [modeKey, liveDealKey]);

  // Sample deal
  const sample = useDealCardEditorSampleDeal(environment, dealType);

  // Live deals list
  const liveList = useDealCardEditorLiveDeals(
    environment,
    mode === "live" ? dealType : null,
    liveSearch
  );

  // Auto-select first live deal if none selected
  useEffect(() => {
    if (mode === "live" && !selectedLiveDealId && liveList.deals.length > 0) {
      setSelectedLiveDealId(liveList.deals[0].deal_id);
    }
  }, [mode, selectedLiveDealId, liveList.deals, setSelectedLiveDealId]);

  // Single live deal
  const liveDeal = useDealCardEditorLiveDeal(
    environment,
    mode === "live" ? selectedLiveDealId : null
  );

  // Resolve active deal
  const resolved = useMemo(() => {
    if (mode === "live") {
      return {
        deal: liveDeal.deal,
        meta: liveDeal.meta,
        loading: liveDeal.loading || liveList.loading,
        error: liveDeal.error || liveList.error,
      };
    }
    return {
      deal: sample.deal,
      meta: sample.meta,
      loading: sample.loading,
      error: sample.error,
    };
  }, [mode, sample, liveDeal, liveList.loading, liveList.error]);

  const refetch = useCallback(() => {
    if (mode === "live") {
      liveList.refetch();
    } else {
      sample.refetch();
    }
  }, [mode, sample, liveList]);

  return useMemo(() => ({
    mode,
    setMode,
    deal: resolved.deal,
    meta: resolved.meta,
    loading: resolved.loading,
    error: resolved.error,
    refetch,
    liveDeals: liveList.deals,
    liveDealsLoading: liveList.loading,
    liveSearch,
    setLiveSearch,
    selectedLiveDealId,
    setSelectedLiveDealId,
  }), [mode, setMode, resolved, refetch, liveList.deals, liveList.loading, liveSearch, selectedLiveDealId, setSelectedLiveDealId]);
}
